import type {Timeframe, WorkstreamTimeframeRef} from 'client';

import {getTimeframeByName} from './detail/workstreamTimeframe';

type WithTimeframe = {timeframe?: WorkstreamTimeframeRef | null};

const startsAt = (
  timeframe: WorkstreamTimeframeRef,
  timeframes?: readonly Timeframe[]
): number | undefined => {
  const known = getTimeframeByName(timeframe.name, timeframes);
  return known ? Date.parse(known.starts_at) : undefined;
};

/**
 * The timeframe a parent without one is about to join: that of its first new
 * sub-workstream that's in one. Saving puts the parent in it (PR 2.8), so the
 * rest of the new sub-workstreams have to match.
 */
export const getPendingParentTimeframe = (
  parent: WithTimeframe,
  newSubworkstreams: readonly WithTimeframe[]
): WorkstreamTimeframeRef | undefined =>
  parent.timeframe
    ? undefined
    : (newSubworkstreams.find((subworkstream) => subworkstream.timeframe)?.timeframe ?? undefined);

/**
 * Why `candidate` can't become a new sub-workstream of `parent`, or undefined
 * if it can. A parent and child must share a timeframe. A workstream that isn't
 * in one joins the other's when they're linked, so only two different
 * timeframes block. Only new edges follow the rule: existing ones stay.
 */
export const getSubworkstreamTimeframeBlock = (
  parent: WithTimeframe & {name: string},
  candidate: WithTimeframe,
  timeframes?: readonly Timeframe[],
  pendingParentTimeframe?: WorkstreamTimeframeRef
): string | undefined => {
  const parentTimeframe = parent.timeframe ?? pendingParentTimeframe ?? null;
  const candidateTimeframe = candidate.timeframe ?? null;
  if (!parentTimeframe || !candidateTimeframe || parentTimeframe.id === candidateTimeframe.id) {
    return undefined;
  }
  const parentStart = startsAt(parentTimeframe, timeframes);
  const candidateStart = startsAt(candidateTimeframe, timeframes);
  // A later workstream continues this one rather than sitting under it.
  if (parentStart !== undefined && candidateStart !== undefined && candidateStart > parentStart) {
    return `In a different timeframe. To connect it to ${parent.name}, link it as the next version.`;
  }
  return 'In a different timeframe.';
};

/** The rule as a picker's subtitle, e.g. "A5 Sign is in H2 FY26. Sub-workstreams must be …". */
export const getSubworkstreamTimeframeRule = (
  parent: WithTimeframe & {name: string},
  pendingParentTimeframe?: WorkstreamTimeframeRef
): string => {
  if (parent.timeframe) {
    return `${parent.name} is in ${parent.timeframe.name}. Sub-workstreams must be in the same timeframe; one that isn't in any joins ${parent.timeframe.name}.`;
  }
  if (pendingParentTimeframe) {
    return `Saving puts ${parent.name} in ${pendingParentTimeframe.name}, so its new sub-workstreams must be in ${pendingParentTimeframe.name} or in no timeframe.`;
  }
  return `${parent.name} isn't in a timeframe. Adding a sub-workstream that's in one puts ${parent.name} in it too.`;
};

/** Eligible candidates first, keeping each group's order. */
export const sortByTimeframeEligibility = <T>(
  candidates: readonly T[],
  getBlock: (candidate: T) => string | undefined
): T[] => [
  ...candidates.filter((candidate) => !getBlock(candidate)),
  ...candidates.filter((candidate) => Boolean(getBlock(candidate))),
];
